import React, { useEffect, useState } from "react";
import { Typography } from "@material-tailwind/react";
import iphone1 from "../images/buyer.svg";
import iphone2 from "../images/seller.svg";
import { useNavigate } from "react-router-dom";
import { 
  ChevronRightIcon, 
  PlayIcon, 
  UserGroupIcon, 
  ShoppingBagIcon,
  PlusIcon,
  EyeIcon,
  SparklesIcon,
  StarIcon,
  DeviceMobileIcon
} from "@heroicons/react/outline";

const Hero = () => {
  const [toggleState, setToggleState] = useState(2);
  const [isHovered, setIsHovered] = useState(null);
  const navigate = useNavigate();

  const toggleTab = (index) => {
    setToggleState(index);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (toggleState === 2) {
        setToggleState(1);
      } else {
        setToggleState(2);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [toggleState]);

  const handleGetQuotes = () => {
    navigate('/create-rfq');
  };

  const handlePostNeed = () => {
    navigate('/create-rfq');
  };

  const handleBrowseRequests = () => {
    navigate('/market-feed');
  };

  const handleDownloadApp = () => {
    // Detect platform and open appropriate app store
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/android/i.test(userAgent)) {
      // Android - Google Play Store
      window.open('https://play.google.com/store/apps/details?id=com.twerceapp.twerce', '_blank');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      // iOS - App Store
      window.open('https://apps.apple.com/app/twerce/id123456789', '_blank');
    } else {
      // Desktop - show both options
      const choice = window.confirm(
        'Choose your platform:\n\nOK - Download for Android (Google Play)\nCancel - Download for iOS (App Store)'
      );
      
      if (choice) {
        window.open('https://play.google.com/store/apps/details?id=com.twerceapp.twerce', '_blank');
      } else {
        window.open('https://apps.apple.com/app/twerce/id123456789', '_blank');
      }
    }
  };

  return (
    <section className="relative py-12 lg:py-20 flex items-center bg-gradient-to-br from-gray-50 via-white to-orange-50 overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated gradient orbs */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200 to-orange-400 rounded-full opacity-20 blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200 to-blue-400 rounded-full opacity-20 blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-purple-200 to-purple-400 rounded-full opacity-10 blur-3xl animate-pulse delay-500"></div>
        
        {/* Floating particles */}
        <div className="absolute top-20 left-20 w-2 h-2 bg-orange-400 rounded-full opacity-60 animate-bounce"></div>
        <div className="absolute top-40 right-40 w-3 h-3 bg-blue-400 rounded-full opacity-60 animate-bounce delay-1000"></div>
        <div className="absolute bottom-20 left-1/3 w-2 h-2 bg-purple-400 rounded-full opacity-60 animate-bounce delay-2000"></div>
        <div className="absolute bottom-40 right-1/3 w-3 h-3 bg-orange-400 rounded-full opacity-60 animate-bounce delay-3000"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content Section */}
          <div className="text-center lg:text-left">
            {/* Enhanced Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-100 to-orange-200 text-orange-700 rounded-full text-sm font-semibold mb-8 shadow-lg border border-orange-200">
              <SparklesIcon className="w-4 h-4 mr-2" />
              <span className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></span>
              #1 B2B Procurement Platform in Nigeria
            </div>

            {/* Enhanced Interactive Role Selection Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div 
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-500 transform ${
                  toggleState === 2 
                    ? "bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 text-white shadow-2xl scale-105 rotate-1" 
                    : "bg-white hover:shadow-xl border-2 border-gray-100 hover:border-orange-200 hover:scale-105"
                }`}
                onClick={() => toggleTab(2)}
                onMouseEnter={() => setIsHovered(2)}
                onMouseLeave={() => setIsHovered(null)}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`p-3 rounded-xl transition-all duration-300 ${
                    toggleState === 2 
                      ? 'bg-white/20 shadow-lg' 
                      : 'bg-gradient-to-br from-orange-100 to-orange-200 shadow-md'
                  }`}>
                    <ShoppingBagIcon className={`w-6 h-6 ${toggleState === 2 ? 'text-white' : 'text-orange-600'}`} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold transition-all duration-300 ${toggleState === 2 ? 'text-white' : 'text-gray-900'}`}>
                      Get Offers Fast
                    </h3>
                    <p className={`text-sm transition-all duration-300 ${toggleState === 2 ? 'text-orange-100' : 'text-gray-600'}`}>
                      Save Time & Money
                    </p>
                  </div>
                </div>
                <p className={`text-sm transition-all duration-300 ${toggleState === 2 ? 'text-orange-100' : 'text-gray-600'}`}>
                  Stop overpaying. Get 5+ competitive quotes in 24 hours. 40% average savings.
                </p>
                {isHovered === 2 && toggleState !== 2 && (
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-orange-600/10 rounded-2xl animate-pulse"></div>
                )}
              </div>
              
              <div 
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-500 transform ${
                  toggleState === 1 
                    ? "bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-2xl scale-105 -rotate-1" 
                    : "bg-white hover:shadow-xl border-2 border-gray-100 hover:border-blue-200 hover:scale-105"
                }`}
                onClick={() => toggleTab(1)}
                onMouseEnter={() => setIsHovered(1)}
                onMouseLeave={() => setIsHovered(null)}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`p-3 rounded-xl transition-all duration-300 ${
                    toggleState === 1 
                      ? 'bg-white/20 shadow-lg' 
                      : 'bg-gradient-to-br from-blue-100 to-blue-200 shadow-md'
                  }`}>
                    <UserGroupIcon className={`w-6 h-6 ${toggleState === 1 ? 'text-white' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold transition-all duration-300 ${toggleState === 1 ? 'text-white' : 'text-gray-900'}`}>
                      Discover Business Opportunities
                    </h3>
                    <p className={`text-sm transition-all duration-300 ${toggleState === 1 ? 'text-blue-100' : 'text-gray-600'}`}>
                      Win More Business
                    </p>
                  </div>
                </div>
                <p className={`text-sm transition-all duration-300 ${toggleState === 1 ? 'text-blue-100' : 'text-gray-600'}`}>
                  Beat competitors to deals. Get qualified leads instantly. 70% faster closure.
                </p>
                {isHovered === 1 && toggleState !== 1 && (
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-2xl animate-pulse"></div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="content-tab">
              {TabContentHandler[toggleState]}
            </div>

            {/* Enhanced Interactive Feature Buttons */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-8">
              <button 
                onClick={handleDownloadApp}
                className="group flex items-center px-4 py-3 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <DeviceMobileIcon className="w-4 h-4 mr-2 text-blue-600 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-sm font-medium">Download App</span>
              </button>
              
              <button 
                onClick={handlePostNeed}
                className="group flex items-center px-4 py-3 bg-white border-2 border-gray-200 rounded-xl hover:border-orange-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <PlusIcon className="w-4 h-4 mr-2 text-orange-600 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-sm font-medium">Post Your Need</span>
              </button>
              
              <button 
                onClick={handleBrowseRequests}
                className="group flex items-center px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white border-2 border-purple-600 rounded-xl hover:from-purple-700 hover:to-purple-800 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <EyeIcon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-sm font-medium">Browse Requests</span>
              </button>
            </div>

            {/* Enhanced CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mt-10">
              <button 
                onClick={handleGetQuotes}
                className="group bg-gradient-to-r from-CC5500 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 flex items-center justify-center border-2 border-transparent hover:border-orange-400"
              >
                <StarIcon className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-200" />
                GET QUOTES NOW
                <ChevronRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform duration-200" />
                </button>
              
              <button className="group border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold text-lg hover:border-CC5500 hover:text-CC5500 hover:bg-orange-50 transition-all duration-300 flex items-center justify-center hover:-translate-y-1">
                <PlayIcon className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                See How It Works
              </button>
            </div>

            {/* Enhanced Trust Indicators */}
            <div className="mt-12 text-center lg:text-left">
              <p className="text-gray-500 text-sm mb-6 font-medium">Trusted by 5,000+ businesses across Nigeria</p>
              <div className="flex justify-center lg:justify-start space-x-8">
                <div className="text-center group">
                  <div className="text-3xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-200">4.8★</div>
                  <div className="text-xs text-gray-500 font-medium">App Store</div>
                </div>
                <div className="text-center group">
                  <div className="text-3xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-200">4.7★</div>
                  <div className="text-xs text-gray-500 font-medium">Google Play</div>
                </div>
                <div className="text-center group">
                  <div className="text-3xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-200">24/7</div>
                  <div className="text-xs text-gray-500 font-medium">Support</div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Image Section */}
          <div className="relative">
            <div className="relative z-10">
              {TabContentImagehandler[toggleState]}
            </div>
            
            {/* Enhanced Floating Elements */}
            <div className="absolute top-10 -right-10 w-20 h-20 bg-gradient-to-br from-orange-200 to-orange-400 rounded-full opacity-60 animate-pulse shadow-lg"></div>
            <div className="absolute bottom-10 -left-10 w-16 h-16 bg-gradient-to-br from-blue-200 to-blue-400 rounded-full opacity-60 animate-pulse delay-1000 shadow-lg"></div>
            <div className="absolute top-1/2 -right-5 w-12 h-12 bg-gradient-to-br from-purple-200 to-purple-400 rounded-full opacity-40 animate-pulse delay-2000 shadow-lg"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

const HeroTabContentImage = ({ url, toggleState }) => (
  <div className={`transition-all duration-700 ${
    toggleState === 1 || toggleState === 2 ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-95 rotate-2"
  }`}>
    <div className="relative group">
      <img 
        src={url} 
        alt={`hero-phone-${toggleState}`} 
        className="w-full max-w-md mx-auto lg:mx-0 drop-shadow-2xl group-hover:scale-105 transition-transform duration-500"
      />
      {/* Enhanced phone glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-orange-400/30 via-purple-400/20 to-blue-400/30 rounded-3xl blur-3xl -z-10 group-hover:blur-2xl transition-all duration-500"></div>
      
      {/* Floating notification dots */}
      <div className="absolute top-8 right-8 w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-lg"></div>
      <div className="absolute top-16 right-12 w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-1000 shadow-lg"></div>
    </div>
  </div>
);

const HeroTabContent = ({ toggleState, title, subTitle, features, ctaText }) => (
  <div className={`transition-all duration-700 ${
    toggleState === 1 || toggleState === 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
  }`}>
    <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
      {title}
    </h1>
    <Typography
      variant="p"
      className="text-lg lg:text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl"
      style={{ fontFamily: 'Open Sans, sans-serif' }}
    >
      {subTitle}
    </Typography>
    
    {/* Enhanced Features List */}
    {features && (
      <div className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <div key={index} className="flex items-center text-gray-700 group">
            <div className="w-3 h-3 bg-gradient-to-r from-CC5500 to-orange-500 rounded-full mr-4 shadow-md group-hover:scale-125 transition-transform duration-200"></div>
            <span className="text-base group-hover:text-gray-900 transition-colors duration-200" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              {feature}
            </span>
          </div>
        ))}
      </div>
    )}

    {/* Enhanced CTA Text */}
    {ctaText && (
      <div className="bg-gradient-to-r from-CC5500/10 via-orange-500/10 to-CC5500/10 rounded-2xl p-6 mb-6 border border-orange-200/50 shadow-lg">
        <p className="text-CC5500 font-semibold text-lg text-center" style={{ fontFamily: 'Open Sans, sans-serif' }}>
          ✨ {ctaText}
        </p>
      </div>
    )}
  </div>
);

const TabContentHandler = {
  2: (
    <HeroTabContent
      toggleState={1}
      title="Stop Overpaying for Supplies"
      subTitle="Tired of calling 20+ suppliers? Post your need once and let verified vendors compete for your business. Save 40% on average while getting 5+ competitive offers in 24 hours."
      features={[
        "Post your need in under 2 minutes - no more endless supplier calls",
        "Get 5+ competitive offers in 24 hours, not weeks",
        "All suppliers pre-verified with 4.8★ ratings",
        "Average 40% cost savings vs traditional procurement methods"
      ]}
      ctaText="Join 300+ companies already saving time and money every month"
    />
  ),
  1: (
    <HeroTabContent
      toggleState={2}
      title="Don't Let Competitors Steal Your Deals"
      subTitle="Get instant notifications when businesses need your products. Be the first to respond and close deals 70% faster than traditional sales methods. Stop losing opportunities to faster competitors."
      features={[
        "Real-time lead notifications within 30 seconds of posting",
        "Pre-qualified prospects ready to buy - no cold calling",
        "Direct messaging with decision-makers, not gatekeepers",
        "Proven track record: 70% faster deal closure than traditional sales"
      ]}
      ctaText="Join 500+ suppliers already winning more business daily"
    />
  ),
};

const TabContentImagehandler = {
  2: <HeroTabContentImage url={iphone1} toggleState={2} />,
  1: <HeroTabContentImage url={iphone2} toggleState={1} />,
};
